<ng-template #labelWithButtons>
  <div
    class="label-with-edit-container"
    appThrottleClick
    (throttledClick)="openEditor()"
  >
    <b
      class="attribute-name"
      [innerHTML]="
        (useDescription ? flatAttribute?.description : flatAttribute?.name)
          | safeHtml
      "
    ></b>
    <div class="button-inline-container">
      @if (editable) {
        <button
          mat-icon-button
          [attr.aria-label]="'Edit ' + flatAttribute?.name"
        >
          <mat-icon class="mat-icon material-icons md-18">edit</mat-icon>
        </button>
      }
      @if (shouldShowHistoricText()) {
        <button
          appThrottleClick
          mat-icon-button
          [attr.aria-label]="getInfoTooltipText()"
          (throttledClick)="showHistoricalText()"
        >
          <mat-icon
            matTooltip="{{ getInfoTooltipText() }}"
            matTooltipPosition="before"
            class="mat-icon material-icons md-18"
            [class.yellow]="isStale()"
            >info</mat-icon
          >
        </button>
      }
      @if (
        flatAttribute?.isEncryptedField &&
        flatAttribute?.hasAttributes &&
        isProfileEditorMode()
      ) {
        <button
          appThrottleClick
          mat-icon-button
          matTooltip="{{ showPassword ? 'Hide value' : 'Show value' }}"
          matTooltipPosition="before"
          [attr.aria-label]="
            showPassword
              ? 'Hide ' + flatAttribute?.name
              : 'Show ' + flatAttribute?.name
          "
          (click)="togglePasswordVisibility(flatAttribute.values)"
        >
          <mat-icon class="mat-icon material-icons md-18">{{
            visibility_icon
          }}</mat-icon>
        </button>
      }
    </div>
  </div>
</ng-template>

@if (!flatAttribute?.isComplexField) {
  @if (!flatAttribute?.isDocument) {
    <div class="editor-container">
      @if (this.isEditing) {
        <div class="attribute-editor-container">
          <attributes-editor
            (blurred)="doSave($event)"
            [mode]="attributeEditorMode"
            [dataSource]="editorDataSource"
          >
            <div class="button-inline-container">
              <button
                mat-button
                color="primary"
                [disabled]="!formIsValid() || isSaving"
                appThrottleClick
                (throttledClick)="doSave()"
              >
                <mat-icon>check</mat-icon> Save
              </button>
              <button
                type="button"
                mat-button
                (mousedown)="preventDefault($event)"
                (click)="cancel()"
              >
                <mat-icon>close</mat-icon> Cancel
              </button>
            </div>
          </attributes-editor>
        </div>
      } @else {
        <div
          class="attribute-display-container"
          fxLayout="row"
          fxLayoutAlign="space-between"
          (click)="openEditor()"
        >
          <div class="attribute-display-item" fxLayout="column">
            <ng-container *ngTemplateOutlet="labelWithButtons"></ng-container>
            @if (flatAttribute?.isSkill) {
              <skill [selectedSkillLevel]="firstElementValueListItem"></skill>
            }
            @if (flatAttribute?.isStarRating) {
              <app-star-rating-attribute
                [selectedStarRating]="firstElementValueListItem"
              ></app-star-rating-attribute>
            }
            @if (flatAttribute?.isLongAnswer) {
              @for (attribute of flatAttribute?.attributes; track attribute) {
                <div
                  class="attribute-long-answer"
                  [innerHTML]="attribute.value | safeHtml"
                ></div>
              }
            }
            @if (flatAttribute?.isEncryptedField) {
              <span class="attribute-value">{{ encryptedFieldValue }}</span>
            }
            @if (
              !flatAttribute?.isSkill &&
              !flatAttribute?.isStarRating &&
              !flatAttribute?.isLongAnswer &&
              !flatAttribute?.isEncryptedField
            ) {
              @for (value of flatAttribute?.attributes; track value) {
                <span class="attribute-value">{{
                  formatDisplayValue(value)
                }}</span>
              }
            }
            @if (!flatAttribute?.hasAttributes && editable) {
              <span class="attribute-value-no-value">{{ noValueText }}</span>
            }
          </div>
        </div>
      }
    </div>
  }
  @if (flatAttribute?.isDocument) {
    <div class="editor-container document-attribute">
      @if (!isEditing) {
        <div
          class="attribute-display-container"
          fxLayout="row"
          fxLayoutAlign="space-between"
        >
          <div class="attribute-display-item" fxLayout="column">
            <ng-container *ngTemplateOutlet="labelWithButtons"></ng-container>
            @if (flatAttribute?.hasAttributes && editable) {
              <div class="document-present">
                @for (attribute of flatAttribute?.attributes; track attribute) {
                  <div class="attribute-value">
                    @if (flatAttribute?.hasAttributes) {
                      <app-custom-document-viewer
                        [documentAttribute]="attribute"
                        [entityType]="customField.entityType"
                        [entityId]="entityId"
                      ></app-custom-document-viewer>
                    }
                  </div>
                }
              </div>
            }
            @if (!flatAttribute?.hasAttributes && editable) {
              <div
                class="document-absent"
                appThrottleClick
                (throttledClick)="openEditor()"
              >
                <span class="attribute-value-no-value">{{ noValueText }}</span>
              </div>
            }
          </div>
        </div>
      }
      @if (isEditing) {
        <div class="attribute-editor-container">
          <attributes-editor
            (blurred)="doSave($event, true)"
            [dataSource]="editorDataSource"
            [mode]="attributeEditorMode"
            [entityId]="entityId"
          >
            <div class="button-inline-container">
              <button
                type="button"
                mat-button
                (mousedown)="preventDefault($event)"
                (click)="cancel()"
              >
                <mat-icon>close</mat-icon> Close
              </button>
            </div>
          </attributes-editor>
        </div>
      }
    </div>
  }
}
@if (flatAttribute?.isComplexField) {
  <div class="editor-container">
    @if (!isEditing) {
      <div
        class="attribute-display-container"
        fxLayout="row"
        fxLayoutAlign="space-between"
        appThrottleClick
        (throttledClick)="openEditor()"
      >
        <div class="attribute-display-item" fxLayout="column">
          <ng-container *ngTemplateOutlet="labelWithButtons"></ng-container>
          @if (flatAttribute?.hasComplexAttributeValues) {
            <div class="document-present">
              @for (
                complexAttributeValue of flatAttribute?.complexAttributeValues;
                track complexAttributeValue
              ) {
                <div class="attribute-value">
                  <app-complex-field-viewer
                    [complexAttributeValue]="complexAttributeValue"
                  ></app-complex-field-viewer>
                </div>
              }
            </div>
          }
          @if (!flatAttribute?.hasComplexAttributeValues) {
            <div class="document-absent">
              <span class="attribute-value-no-value">{{ noValueText }}</span>
            </div>
          }
        </div>
      </div>
    }
    @if (isEditing) {
      <div class="attribute-editor-container">
        <attributes-editor
          [dataSource]="editorDataSource"
          [mode]="attributeEditorMode"
          [entityId]="entityId"
          (addOrUpdateComplexValue)="addOrUpdateComplexValue.emit($event)"
        >
          <button
            type="button"
            mat-button
            (mousedown)="preventDefault($event)"
            (click)="cancel()"
          >
            <mat-icon>close</mat-icon> Close
          </button>
        </attributes-editor>
      </div>
    }
  </div>
}
